/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { ShadowBox } from './Privacy.style';
import PrivacyWrapper from './PrivacyContainer';
import { Helmet } from 'react-helmet';

export default function PrivacyPolicy({
  isLoggedIn,
  hideBackArrow,
  isAuthPage,
}) {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | WafyNet - Your Data Privacy Matters</title>
        <meta
          name="description"
          content="Read WafyNet's privacy policy to understand how we protect your personal information and respect your privacy rights."
        />
      </Helmet>
      <PrivacyWrapper
        isLoggedIn={isLoggedIn}
        hideBackArrow={hideBackArrow}
        isAuthPage={isAuthPage}
      >
        <ShadowBox isauthpage={isAuthPage}>
          <div style={{ fontSize: 15 }}>
            <h2>Privacy Policy</h2>
            <p>Updated on 2024-07-23</p>

            <div
              style={{
                background: '#fafafa',
                border: '1px solid #e9e9e9',
                borderRadius: 12,
                padding: 16,
                margin: '8px 0 18px',
              }}
            >
              <strong>Summary</strong>
              <p>
                <strong>WafyNet</strong> (“we,” “our,” “us”) respects
                your privacy. We collect the info you provide (name, email,
                phone, billing if applicable), optional camera/gallery content
                you choose to upload, and basic device/usage data (e.g., IP) to
                run and improve WafyNet. We <strong>do not sell</strong> your
                personal data. You can access, update, or delete your account
                info and unsubscribe from marketing at any time. EU/EEA and
                California residents have additional rights under GDPR/CCPA.
              </p>
              <p>
                Contact:{' '}
                <a href="mailto:support@wafynet.com" rel="noreferrer">support@wafynet.com</a> •{' '}
                <a
                  href="https://www.wafynet.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  www.wafynet.com
                </a>
              </p>
            </div>

            <p>
              <strong>WafyNet</strong> (“we,” “our,” or “us”) is
              committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, and disclose your personal information.
            </p>
            <p>
              This Privacy Policy applies to our website,{' '}
              <a href="https://www.wafynet.com" target="_blank" rel="noreferrer">
                www.wafynet.com
              </a>
              , its associated subdomains (collectively, our “Service”), and our
              application, WafyNet. By accessing or using our Service, you agree
              to our collection, storage, use, and disclosure of your personal
              information as described in this Privacy Policy and our Terms of
              Service.
            </p>

            <h3>Definitions and Key Terms</h3>
            <ul>
              <li>
                <strong>Cookie:</strong> A small piece of data saved by your
                browser to identify your browser, provide analytics, and
                remember preferences (e.g., language or login).
              </li>
              <li>
                <strong>Company:</strong> WafyNet, TamilNadu,
                India, responsible for your information under this Privacy
                Policy.
              </li>
              <li>
                <strong>Country:</strong> India, where WafyNet is
                incorporated and operates.
              </li>
              <li>
                <strong>Customer:</strong> A person, company, or organization
                that signs up to use the WafyNet Service.
              </li>
              <li>
                <strong>Device:</strong> Any internet-connected device (e.g.,
                phone, tablet, computer) that can access the Service.
              </li>
              <li>
                <strong>IP Address:</strong> A number assigned to a device
                connected to the Internet that can indicate general location and
                connectivity.
              </li>
              <li>
                <strong>Personnel:</strong> Individuals employed by or
                contracted with WafyNet to perform services.
              </li>
              <li>
                <strong>Personal Data:</strong> Information that directly or
                indirectly identifies a natural person (e.g., name, email,
                identification number).
              </li>
              <li>
                <strong>Service:</strong> The services provided by WafyNet as
                described in our Terms.
              </li>
              <li>
                <strong>Third-Party Service:</strong> Advertisers, partners, and
                service providers whose products/services may be integrated with
                or promoted on the Service.
              </li>
              <li>
                <strong>Website:</strong>{' '}
                <a
                  href="https://www.wafynet.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  www.wafynet.com
                </a>
                .
              </li>
              <li>
                <strong>You:</strong> A person or entity using the Services.
              </li>
            </ul>

            <h3>What Information Do We Collect?</h3>
            <p>
              We collect information when you visit the Service, register, place
              an order, subscribe to a newsletter, respond to a survey, or fill
              out a form. This may include: name/username, phone number, email
              address, mailing/billing address, job titles, and debit/credit
              card details.
            </p>
            <p>From mobile devices (optional, only with your consent):</p>
            <ul>
              <li>
                <strong>Camera (Pictures):</strong> allows you to upload images
                directly. You can deny camera permission and continue to use the
                Service.
              </li>
              <li>
                <strong>Photo Gallery (Pictures):</strong> allows you to upload
                images from your device. You can deny gallery access and
                continue to use the Service.
              </li>
            </ul>

            <h3>How Do We Use The Information We Collect?</h3>
            <ul>
              <li>To personalize your experience and respond to your needs.</li>
              <li>To improve the Service based on feedback and usage.</li>
              <li>To improve customer support and communications.</li>
              <li>To process transactions.</li>
              <li>To administer promotions, surveys, and features.</li>
              <li>
                To send periodic emails and service notifications (you can opt
                out of marketing emails at any time).
              </li>
            </ul>

            <h3>When Do We Use Customer Information From Third Parties?</h3>
            <p>
              We may receive information from third parties (e.g., automated
              fraud detection services) or publicly available sources (e.g.,
              social media), subject to your settings with those services.
            </p>
            <h3>How Do We Use Your Email Address?</h3>
            <p>
              By submitting your email on our Service, you agree to receive
              emails from us. You can unsubscribe at any time via the link in
              those emails. We only email people who have authorized us to
              contact them directly or via a third party. We may also use your
              email for customer audience targeting on platforms (e.g., to show
              relevant ads) where permitted. Emails submitted solely for order
              processing are used only for that purpose.
            </p>

            <h3>Could My Information Be Transferred To Other Countries?</h3>
            <p>
              We are incorporated in India. Information collected via the
              Website, directly from you, or through support may be transferred
              to our offices, personnel, or trusted providers around the world
              and may be viewed or hosted in countries that may not have the
              same data protection laws. By using the Service, you consent to
              such <em>trans-border</em> transfers as permitted by applicable
              law.
            </p>

            <h3>Is The Information Collected Through Our Service Secure?</h3>
            <p>
              We use reasonable technical and organizational measures (e.g.,
              SSL, encryption in transit where applicable, access controls) to
              protect your information. No method of transmission or storage is
              100% secure. Where applicable law imposes duties to protect
              personal information, intentional misconduct will be the standard
              used to assess compliance.
            </p>

            <h3>Can I Update or Correct My Information?</h3>
            <p>
              You may contact us to (1) update or correct personal information,
              (2) change communication preferences, or (3) delete personal
              information by closing your account (subject to legal/operational
              retention). We may take reasonable steps to verify your identity
              before acting on such requests. Backups/archives may retain copies
              for a limited time.
            </p>

            <h3>Personnel</h3>
            <p>
              If you are a worker or applicant, we collect information you
              voluntarily provide for HR purposes, benefits administration, and
              applicant screening. You may contact us to update, correct, or
              obtain a record of your information subject to applicable law.
            </p>

            <h3>Affiliates</h3>
            <p>
              We may disclose information to our corporate affiliates (entities
              under common control). They will treat your information in
              accordance with this Privacy Policy.
            </p>

            <h3>How Long Do We Keep Your Information?</h3>
            <p>
              We keep information only as long as needed to provide the Service
              and for the purposes described here. When no longer needed, we
              delete or de-identify information unless retention is required by
              law.
            </p>

            <h3>How Do We Protect Your Information?</h3>
            <p>
              We use a secure server for sensitive transactions. Data
              transmitted using SSL may be encrypted into payment gateway
              databases and accessible only to authorized personnel with special
              access rights who are required to keep it confidential. We do not
              store full credit card numbers after a transaction. However, no
              system can guarantee absolute security.
            </p>

            <h3>Governing Law</h3>
            <p>
              The laws of India, excluding its conflict-of-laws rules, govern
              this Policy and your use of our Service, subject to any mandatory
              local laws.
            </p>

            <h3>Your Consent</h3>
            <p>
              By using our Service, registering an account, or making a
              purchase, you consent to this Privacy Policy.
            </p>

            <h3>Links to Other Websites</h3>
            <p>
              This Policy applies only to the Service. We are not responsible
              for the content or privacy practices of third-party sites linked
              from the Service. Your use of those sites is governed by their
              policies.
            </p>

            <h3>Cookies</h3>
            <p>
              We use cookies to identify areas of our site you visit, remember
              preferences, and improve performance. You can disable cookies in
              your browser, but some features may not work properly. We do not
              place personally identifiable information in cookies.
            </p>

            <h3>Advertising & Remarketing</h3>
            <p>
              Advertising helps keep parts of the Service free. Cookies and
              similar technologies improve ad relevance and measurement. We may
              use remarketing/retargeting to show ads to visitors, in line with
              applicable laws and platform policies.
            </p>

            <h3>Payment Details</h3>
            <p>
              Where applicable, sensitive payment information is handled by
              compliant payment gateways. We do not store full credit card
              numbers on our servers after processing.
            </p>

            <h3>Kids’ Privacy</h3>
            <p>
              We do not knowingly collect personal information from children
              under 13. If you are a parent/guardian and believe your child
              provided us with personal data, please contact us and we will
              delete it. In some jurisdictions the minimum age may be higher
              (e.g., 16); we comply where applicable.
            </p>

            <h3>Changes To This Privacy Policy</h3>
            <p>
              We may update this Policy from time to time. Changes will be
              posted on this page with an updated “Last Updated” date.
            </p>

            <h3>Third-Party Services</h3>
            <p>
              We may display, include, or make available third-party content
              (including data, information, apps, and other products/services)
              or provide links to third-party websites or services (“Third-Party
              Services”). We are not responsible for Third-Party Services, and
              your use of them is at your own risk and subject to their terms
              and policies.
            </p>

            <h3>Tracking Technologies</h3>
            <ul>
              <li>
                <strong>Cookies:</strong> Enhance performance and functionality.
                Without them, certain features (e.g., remembering login) may not
                persist.
              </li>
              <li>
                <strong>Local Storage:</strong> Client-side storage (DOM
                storage) with higher capacity than cookies and not sent in HTTP
                headers.
              </li>
            </ul>

            <h3>Information about GDPR (EU/EEA)</h3>
            <h4 style={{ marginTop: 8 }}>What is GDPR?</h4>
            <p>
              GDPR is an EU-wide privacy and data protection law that regulates
              how EU/EEA residents’ data is processed and protected and enhances
              individuals’ rights over their data.
            </p>
            <h4 style={{ marginTop: 8 }}>Your GDPR Rights</h4>
            <ul>
              <li>
                Access, correction, deletion, restriction, and portability.
              </li>
              <li>
                Objection to processing and withdrawal of consent (where
                applicable).
              </li>
              <li>Right to lodge a complaint with a supervisory authority.</li>
            </ul>
            <p>
              We process/store personal data with vetted, DPA-compliant vendors.
              We store conversation and personal data for up to 6 years unless
              your account is deleted, after which we delete data in accordance
              with this Policy (generally within 60 days unless longer retention
              is required by law).
            </p>

            <h3>California Residents (CCPA)</h3>
            <p>
              CCPA requires disclosure of categories of personal information
              collected, sources, purposes, and third-party sharing—all
              described in this Policy. California residents have the rights to
              Know/Access, Equal Service, Delete, and Opt-Out of Sale. We do not
              sell your personal information.
            </p>

            <h3>California Online Privacy Protection Act (CalOPPA)</h3>
            <p>
              CalOPPA affords similar rights (Know/Access, Equal Service,
              Delete, Opt-Out of Sale). We do not sell personal information.
            </p>

            <h3>Contact Us</h3>
            <p>Questions or requests? We’re here to help.</p>
            <ul>
              <li>
                Email:{' '}
                <a href="mailto:support@wafynet.com">support@wafynet.com</a>
              </li>
              <li>
                Website:{' '}
                <a
                  href="https://www.wafynet.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  www.wafynet.com
                </a>
              </li>
            </ul>
          </div>
        </ShadowBox>
      </PrivacyWrapper>
    </>
  );
}
